import { useCallback, useEffect, useRef, useState } from 'react';
import type { Message } from '@/types';

const INCOMING_NOTICE_GRACE_MS = 2_500;

type UseIncomingMessageNoticeParams = {
  selectedConversationId: string;
  messages: Message[];
  /** Latest preview timestamp from the conversations list row. */
  listLastMessageAt?: string;
};

function threadMatchesListPreview(
  messages: Message[],
  listLastMessageAt?: string
): boolean {
  if (!listLastMessageAt) return false;
  const latestThreadAt = messages[messages.length - 1]?.createdAt;
  if (!latestThreadAt) return false;
  return (
    new Date(latestThreadAt).getTime() >=
    new Date(listLastMessageAt).getTime() - 2_000
  );
}

export function useIncomingMessageNotice({
  selectedConversationId,
  messages,
  listLastMessageAt,
}: UseIncomingMessageNoticeParams) {
  const [showNotice, setShowNotice] = useState(false);
  const pendingNoticeTimeoutRef = useRef<number | null>(null);
  const messageCountAtSignalRef = useRef(0);
  const trackedListLastMessageAtRef = useRef(listLastMessageAt);
  const messagesRef = useRef(messages);
  const listLastMessageAtRef = useRef(listLastMessageAt);

  messagesRef.current = messages;
  listLastMessageAtRef.current = listLastMessageAt;

  const clearPendingNotice = useCallback(() => {
    if (pendingNoticeTimeoutRef.current !== null) {
      window.clearTimeout(pendingNoticeTimeoutRef.current);
      pendingNoticeTimeoutRef.current = null;
    }
  }, []);

  const dismissNotice = useCallback(() => {
    clearPendingNotice();
    setShowNotice(false);
  }, [clearPendingNotice]);

  const signalIncomingMessage = useCallback(() => {
    if (!selectedConversationId) return;

    messageCountAtSignalRef.current = messagesRef.current.length;
    clearPendingNotice();
    setShowNotice(false);

    pendingNoticeTimeoutRef.current = window.setTimeout(() => {
      pendingNoticeTimeoutRef.current = null;

      if (messagesRef.current.length > messageCountAtSignalRef.current) {
        return;
      }

      if (
        threadMatchesListPreview(
          messagesRef.current,
          listLastMessageAtRef.current
        )
      ) {
        return;
      }

      setShowNotice(true);
    }, INCOMING_NOTICE_GRACE_MS);
  }, [clearPendingNotice, selectedConversationId]);

  useEffect(() => {
    if (messages.length > messageCountAtSignalRef.current) {
      dismissNotice();
    }
  }, [messages.length, dismissNotice]);

  useEffect(() => {
    if (threadMatchesListPreview(messages, listLastMessageAt)) {
      dismissNotice();
    }
  }, [messages, listLastMessageAt, dismissNotice]);

  useEffect(() => {
    if (!selectedConversationId || !listLastMessageAt) {
      trackedListLastMessageAtRef.current = listLastMessageAt;
      return;
    }

    const previous = trackedListLastMessageAtRef.current;
    trackedListLastMessageAtRef.current = listLastMessageAt;

    if (!previous || previous === listLastMessageAt) return;

    const advanced =
      new Date(listLastMessageAt).getTime() > new Date(previous).getTime();
    if (
      advanced &&
      !threadMatchesListPreview(messagesRef.current, listLastMessageAt)
    ) {
      signalIncomingMessage();
    }
  }, [listLastMessageAt, selectedConversationId, signalIncomingMessage]);

  useEffect(() => {
    dismissNotice();
    trackedListLastMessageAtRef.current = listLastMessageAt;
    messageCountAtSignalRef.current = messages.length;
  }, [
    selectedConversationId,
    dismissNotice,
    listLastMessageAt,
    messages.length,
  ]);

  useEffect(() => () => clearPendingNotice(), [clearPendingNotice]);

  return {
    showIncomingMessageNotice: showNotice,
    signalIncomingMessage,
    dismissIncomingMessageNotice: dismissNotice,
  };
}
